/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1758758400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Sep 25 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "responsys",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Profile lists",
          "depth": 1,
          "id": "profile-lists"
        }, {
          "value": "Profile Extension Tables",
          "depth": 1,
          "id": "profile-extension-tables"
        }, {
          "value": "Custom / REI Events",
          "depth": 1,
          "id": "custom--rei-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PremiumDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Oracle Responsys"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Event Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Profile Lists"
              })
            }), _jsx(_components.td, {
              children: "Create base profiles with Email, phone number, or custom IDs"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Profile Extension Tables"
              })
            }), _jsx(_components.td, {
              children: "Add custom attributes and properties to the base profiles"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom / REI Events"
              })
            }), _jsx(_components.td, {
              children: "Add items to Events"
            }), _jsx(_components.td, {
              children: "Insert, Update"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["To use the Responsys destination in Hightouch, you need to know your Responsys credentials and which Oracle system hosts your\nResponsys account. To find your host URL refer to the ", _jsx(_components.a, {
          href: "https://docs.oracle.com/en/cloud/saas/marketing/responsys-rest-api/SendRequests.html",
          children: "Oracle Responsys documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "profile-lists",
        children: "Profile lists"
      }), "\n", _jsx(_components.p, {
        children: "Syncing to Responsys profile lists lets you create base profiles. Base profiles primarily contain user IDs to associate\nthe user to records in profile extension tables."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Responsys on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Mobile number"
        }), "\n", _jsx(_components.li, {
          children: "Customer ID"
        }), "\n", _jsx(_components.li, {
          children: "Responsys ID (RIID)"
        }), "\n", _jsx(_components.li, {
          children: "MD5 Hashed Email"
        }), "\n", _jsx(_components.li, {
          children: "SHA256 Hashed Email"
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Hashed emails can't be used for upsert operations."
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map additional user information such as other identifiers, country, address, or other user-created fields."
      }), "\n", _jsx(_components.h3, {
        id: "profile-extension-tables",
        children: "Profile Extension Tables"
      }), "\n", _jsx(_components.p, {
        children: "Syncing to profile extension tables lets you attach additional information to base user profiles.\nProfile extension tables (PETs) give you a way to store additional attributes that define behavioral, demographic or\nprofile preference characteristics of your contacts. Using PETs helps you organize information about your contacts\nin a way that is most efficient for your queries and data imports."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "https://docs.oracle.com/en/cloud/saas/marketing/responsys-user/List_CreatePET.htm",
          children: "Responsys Profile Extension Table docs"
        }), " to learn more."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Responsys on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Mobile number"
        }), "\n", _jsx(_components.li, {
          children: "Customer ID"
        }), "\n", _jsx(_components.li, {
          children: "Responsys ID (RIID)"
        }), "\n", _jsx(_components.li, {
          children: "MD5 Hashed Email"
        }), "\n", _jsx(_components.li, {
          children: "SHA256 Hashed Email"
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Hashed emails can't be used for upsert operations."
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to any user-created field in your Responsys PET."
      }), "\n", _jsx(_components.h3, {
        id: "custom--rei-events",
        children: "Custom / REI Events"
      }), "\n", _jsx(_components.p, {
        children: "Syncing to Responsys Custom Events (part of Responsys Event Infrastructure or REI) allows you to track and store behavioral data about your contacts.\nThese events capture specific actions or interactions that contacts have with your brand, such as purchases, website visits, app usage, or any custom activities you want to monitor."
      }), "\n", _jsxs(_components.p, {
        children: ["API Documentation on Responsys events is available ", _jsx(_components.a, {
          href: "https://docs.oracle.com/en/cloud/saas/marketing/responsys-rest-api/api-events.html",
          children: "here"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Responsys on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Event Name"
        }), "\n", _jsx(_components.li, {
          children: "Profile List"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync rows to Responsys events on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Recipient ID"
        }), "\n", _jsx(_components.li, {
          children: "Customer ID"
        }), "\n", _jsx(_components.li, {
          children: "Email Address"
        }), "\n", _jsx(_components.li, {
          children: "Mobile Number"
        }), "\n", _jsx(_components.li, {
          children: "MD5 Hashed Email"
        }), "\n", _jsx(_components.li, {
          children: "SHA256 Hashed Email"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
